import { useContext, useEffect, useMemo, useRef } from 'react';
import { useParams, NavLink, useLocation, Routes, Route, Navigate } from 'react-router';
import { TripodContext, TripodContextValue } from '@moment/context/TripodContext.tsx';
import { TabContainer } from '@moment/shared-code/tab-container.ts';
import { IRouteObject } from '@/router/interfaces.ts';
import { NOT_FOUND_PAGE_ROUTE } from '@/pages/Root/router/router.tsx';
import './TabContainerLayout.scss';
import { TabbedPageRenderer } from './TabbedPageRenderer';
import { TabbedSeenUIDsProvider } from './TabbedSeenUIDsContext';
import { TabContainerContext } from './TabContainerContext.tsx';
import { useObservableSessionStorage } from '@moment/hooks/useObservableSessionStorage.ts';
import { useTranslation } from 'react-i18next';

const getSessionKey = (containerName: string) => `tabContainerVisited_${containerName}`;

const TAB_BAR_HEIGHT = 54; // As defined by padding + content height in SCSS

export const TabContainerLayout = () => {
  const { containerName } = useParams<{ containerName: string }>();
  const location = useLocation();
  const { tripodData, isLoading: isTripodLoading } = useContext(
    TripodContext
  ) as TripodContextValue;
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);

  // Determine the initial visibility state. This logic runs once when the hook initializes.
  const getInitialVisibility = () => {
    // Priority 1: On the client, check if the visited state is already stored in sessionStorage.
    if (!import.meta.env.SSR && containerName) {
      const storedValue = sessionStorage.getItem(getSessionKey(containerName));
      if (storedValue !== null) {
        return JSON.parse(storedValue);
      }
    }
    // Priority 2: Fall back to checking the URL parameter for the very first visit.
    const searchParams = new URLSearchParams(location.search);
    const isFsdMedium = searchParams.get('medium') === 'fsd';
    // If it's an FSD visit, the bar is initially hidden. Otherwise, it's visible.
    return !isFsdMedium;
  };

  const [isBarVisible, setBarVisible] = useObservableSessionStorage(
    containerName
      ? getSessionKey(containerName)
      : 'tabContainerVisited_unknown',
    getInitialVisibility()
  );

  const navBarClassVisibility = useMemo(
    () => (isBarVisible ? 'visible' : ''),
    [isBarVisible]
  );

  const hasBeenHidden = useRef(false);
  // Ref to store the timestamp of the last 'visible' event to detect rapid visibility changes.
  const lastVisibleTimestamp = useRef(0);

  const VISIBILITY_FLASH_THRESHOLD_MS = 900; // Time in ms to consider a rapid visible->hidden change a "flash".

  // This effect acts as a safeguard to correct any SSR hydration mismatch.
  // It ensures the 'visible' class on the nav element is in sync with the isBarVisible state.
  useEffect(() => {
    if (navRef.current) {
      if (isBarVisible && !navRef.current.classList.contains('visible')) {
        navRef.current.classList.add('visible');
      }
    }
  }, [isBarVisible]);

  // Effect to show the bar on interaction and mark as visited (only if currently hidden).
  useEffect(() => {
    const showBarAndMarkVisited = () => {
      setBarVisible(true);
    };

    if (!isBarVisible) {
      // Show on first touch interaction
      window.addEventListener('touchstart', showBarAndMarkVisited, {
        once: true,
      });

      // Also show if the user backgrounds the app and comes back
      const handleVisibilityChange = () => {
        // When the app becomes visible, record the timestamp.
        if (document.visibilityState === 'visible') {
          lastVisibleTimestamp.current = Date.now();
          // If we were previously marked as hidden, show the bar.
          if (hasBeenHidden.current) {
            console.debug(
              `[CMBU-2187] App is visible again and was hidden. Showing bar.`
            );
            showBarAndMarkVisited();
          }
          return;
        }

        if (document.visibilityState === 'hidden') {
          const timeSinceVisible = Date.now() - lastVisibleTimestamp.current;
          // If the app became hidden very shortly after becoming visible, it's likely a system overlay flash. Ignore it.
          if (timeSinceVisible < VISIBILITY_FLASH_THRESHOLD_MS) {
            console.debug(
              `[CMBU-2187] 'hidden' event occurred too quickly (${timeSinceVisible}ms) after 'visible'. Ignoring.`
            );
            return;
          }
          console.debug(
            `[CMBU-2187] App hidden after a significant duration. Marking.`
          );
          hasBeenHidden.current = true;
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener('touchstart', showBarAndMarkVisited);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    }
  }, [isBarVisible, setBarVisible]);

  // The container data is now derived directly and reliably from the TripodContext.
  const container = useMemo(
    () =>
      tripodData?.tabContainers?.find(
        (tc: TabContainer) => tc.name === containerName
      ),
    [tripodData, containerName]
  );
  const dynamicRoutes = tripodData?.dynamicRoutes || [];

  // Memoize pageRoutes to prevent re-calculation on every render.
  // This stabilizes the props passed to the <Routes> component.
  // It now includes both tabbed pages and any other dynamic page that could be a child.
  const pageRoutes: IRouteObject[] = useMemo(() => {
    if (!container) return [];

    // Make all dynamic routes potential children of this container
    // by making their paths relative (e.g., '/feed' -> 'feed').
    return dynamicRoutes.map((route) => ({
      ...route,
      path: route.path.substring(1), // Make path relative, e.g., '/feed' -> 'feed'
    }));
  }, [container, dynamicRoutes]);

  // Find the default route; fallback to the first tab if not set or not found
  // Memoize this calculation as well to ensure the <Navigate> component receives a stable `to` prop.
  const finalDefaultRoute = useMemo(() => {
    if (!container) {
      return pageRoutes.length === 0 ? undefined : pageRoutes[0];
    }
    const defaultRoute = container.defaultTabId
      ? pageRoutes.find(
          (r) =>
            r.id ===
            container.tabs.find((t) => t.id === container.defaultTabId)?.pageId
        )
      : pageRoutes[0];
    return defaultRoute || pageRoutes[0];
  }, [container, pageRoutes]);

  const currentBasePath = `/tc/${containerName}`;

  if (isTripodLoading && !container) {
    // Show loading only if we truly have no data yet
    return (
      <div className="tab-container-loading">
        <div className="spinner" role="progressbar" />
      </div>
    );
  }

  if (pageRoutes.length === 0) {
    return <div>This Tab Container has no valid pages configured.</div>;
  }

  if (!container) {
    return <div>Something has gone wrong. Container not found.</div>;
  }

  if (!finalDefaultRoute) {
    return <div>Something has gone wrong.</div>;
  }

  return (
    <div className="tab-container-layout">
      <TabContainerContext.Provider
        value={{
          isInTabContainer: true,
          tabBarHeight: TAB_BAR_HEIGHT,
          isBarVisible,
        }}
      >
        <TabbedSeenUIDsProvider>
          <div className="tab-container-content">
            <Routes>
              {pageRoutes.map((route) => {
                return (
                  <Route
                    key={route.id}
                    path={route.path}
                    element={
                      <TabbedPageRenderer
                        pageDataMap={tripodData?.pageDataMap || {}}
                        pageId={route.id!}
                      />
                    }
                  />
                );
              })}
              {/* Default route for the container: redirects /tc/container-name to the first tab's page */}
              <Route
                index
                element={
                  <Navigate
                    to={`${finalDefaultRoute.path}${location.search}`}
                    replace
                  />
                }
              />
              <Route path="*" element={NOT_FOUND_PAGE_ROUTE.element} />
            </Routes>
          </div>
        </TabbedSeenUIDsProvider>
      </TabContainerContext.Provider>

      <nav
        ref={navRef}
        className={`tab-container-nav ${navBarClassVisibility}`}
      >
        {container.tabs.map((tab) => {
          const route = pageRoutes.find((r) => r.id === tab.pageId);
          if (!route) return null;

          const tabPath = `${currentBasePath}/${route.path}`;

          return (
            <NavLink key={tab.id} to={tabPath} className="tab-item" replace={true}>
              <span
                className="tab-item-icon"
                dangerouslySetInnerHTML={{ __html: tab.svg }}
              />
              <span className="tab-item-label">{t(tab.name)}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};
